import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, KeyRound, Users2, Save, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

/** Password gate for Hot Leads / Finance. Once unlocked, the check persists
 *  in sessionStorage under `vault-unlock:<name>` — cleared automatically when
 *  the browser tab closes or the user logs out, so every fresh session
 *  requires the password again. */

const isUnlocked = (name) => {
  try {
    return sessionStorage.getItem(`vault-unlock:${name}`) === "ok";
  } catch (_) { return false; }
};

export default function VaultGate({ name, label, children }) {
  const { user } = useAuth();
  const [ready, setReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ configured: false, is_admin: false, allowed_users: [] });
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Admin-only settings state
  const [newPw, setNewPw] = useState("");
  const [users, setUsers] = useState([]);
  const [allowedIds, setAllowedIds] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get(`/vaults/${name}`);
      setStatus(data);
      setAllowedIds((data.allowed_users || []).map((u) => u.id));
      // If the admin never set a password, treat as unlocked automatically
      if (!data.configured) {
        setUnlocked(true);
        sessionStorage.setItem(`vault-unlock:${name}`, "ok");
      } else if (isUnlocked(name)) {
        setUnlocked(true);
      }
    } catch (_) { /* silent */ } finally { setReady(true); }
  };

  useEffect(() => {
    refreshStatus();
    if (user?.is_super_admin || user?.is_admin) {
      api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const submitUnlock = async (e) => {
    e?.preventDefault?.();
    if (!pw) return;
    setBusy(true);
    try {
      await api.post(`/vaults/${name}/unlock`, { password: pw });
      sessionStorage.setItem(`vault-unlock:${name}`, "ok");
      setUnlocked(true);
      setPw("");
      toast.success(`${label} unlocked for this session`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Incorrect password");
    } finally { setBusy(false); }
  };

  const lockNow = () => {
    sessionStorage.removeItem(`vault-unlock:${name}`);
    setUnlocked(false);
  };

  const saveNewPassword = async () => {
    if (!newPw || newPw.length < 4) return toast.error("Password must be at least 4 characters");
    setSavingSettings(true);
    try {
      await api.post(`/vaults/${name}/set-password`, { password: newPw });
      toast.success("Password updated");
      setNewPw("");
      await refreshStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    } finally { setSavingSettings(false); }
  };

  const saveAllowlist = async () => {
    setSavingSettings(true);
    try {
      await api.post(`/vaults/${name}/allowlist`, { allowed_user_ids: allowedIds });
      toast.success("Access list saved");
      await refreshStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    } finally { setSavingSettings(false); }
  };

  const toggleUser = (id) =>
    setAllowedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (!ready) return <div className="p-6 text-white/50 text-sm">Checking access…</div>;

  // Explicit "no access" state — user isn't an admin and isn't on the vault
  // allowlist, so drop them at a friendly card instead of the password prompt
  // (which they couldn't unlock even with the right password because the
  // backend endpoints would still 403 them).
  if (!status.can_access && !status.is_admin) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-lg border border-white/10 bg-black/40 p-6 text-center" data-testid={`vault-no-access-${name}`}>
          <div className="mx-auto h-12 w-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 mb-3">
            <Lock size={18} />
          </div>
          <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40">Restricted</div>
          <h2 className="text-2xl font-display mt-1">{label}</h2>
          <p className="text-xs text-white/50 mt-2 leading-relaxed">
            You don&apos;t have access to this section. Ask Amit or an Admin to add you to the {label.toLowerCase()} vault.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Tiny admin/lock header shown even when unlocked so admins can manage
          the vault without re-locking. */}
      {(unlocked && (status.is_admin || status.configured)) && (
        <div className="px-4 sm:px-6 pt-3 -mb-3 flex items-center justify-end gap-2 text-[0.7rem] uppercase tracking-widest">
          {status.configured && (
            <button onClick={lockNow} className="text-white/40 hover:text-white flex items-center gap-1" data-testid={`vault-lock-btn-${name}`}>
              <Lock size={10} /> Lock
            </button>
          )}
          {status.is_admin && (
            <button onClick={() => setSettingsOpen(true)} className="text-white/40 hover:text-white flex items-center gap-1" data-testid={`vault-settings-btn-${name}`}>
              <KeyRound size={10} /> Vault settings
            </button>
          )}
        </div>
      )}

      {unlocked ? children : (
        <div className="min-h-[70vh] flex items-center justify-center p-6">
          <form onSubmit={submitUnlock} className="w-full max-w-sm rounded-lg border border-white/10 bg-black/40 p-6 space-y-4" data-testid={`vault-gate-${name}`}>
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-[#E11D2E]/10 border border-[#E11D2E]/40 flex items-center justify-center text-[#E11D2E] mb-3">
                <Lock size={18} />
              </div>
              <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40">Vault</div>
              <h2 className="text-2xl font-display mt-1">{label}</h2>
              <p className="text-xs text-white/50 mt-1">Enter the {label.toLowerCase()} password to view this section this session.</p>
            </div>
            <div>
              <Label className="overline">Password</Label>
              <Input
                type="password"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid={`vault-password-${name}`}
                className="bg-black/40 border-white/10 mt-1.5"
              />
            </div>
            <Button type="submit" disabled={busy || !pw} className="w-full bg-[#E11D2E] hover:bg-[#F03041]" data-testid={`vault-submit-${name}`}>
              {busy ? "Checking…" : "Unlock"}
            </Button>
            {status.is_admin && (
              <button type="button" onClick={() => setSettingsOpen(true)} className="w-full text-xs text-white/40 hover:text-white text-center flex items-center justify-center gap-1" data-testid={`vault-settings-fallback-${name}`}>
                <KeyRound size={10} /> Manage vault
              </button>
            )}
          </form>
        </div>
      )}

      {/* Admin settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <KeyRound size={16} /> {label} vault settings
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Change the password + pick who else can unlock this section. Super Admin + Admins always bypass.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <Label className="overline">
                {status.configured ? "Change password" : "Set password (currently open)"}
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 4 characters" data-testid={`vault-set-password-${name}`} className="bg-black/40 border-white/10 flex-1" />
                <Button onClick={saveNewPassword} disabled={savingSettings || !newPw} className="bg-[#E11D2E] hover:bg-[#F03041]" data-testid={`vault-save-password-${name}`}>
                  <Save size={13} className="mr-1" /> Save
                </Button>
              </div>
            </div>

            <div>
              <div className="overline flex items-center justify-between mb-2">
                <span className="flex items-center gap-1"><Users2 size={11} /> Who can access</span>
                <span className="text-[0.7rem] normal-case tracking-normal text-white/40">{allowedIds.length} selected</span>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-white/10 bg-black/20 divide-y divide-white/5" data-testid={`vault-users-${name}`}>
                {users.map((u) => {
                  const on = allowedIds.includes(u.id);
                  return (
                    <label key={u.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03] ${on ? "bg-[#E11D2E]/5" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleUser(u.id)} className="accent-[#E11D2E]" data-testid={`vault-user-${name}-${u.id}`} />
                      <span className="text-sm truncate flex-1">{u.name}</span>
                      <span className="text-[0.7rem] text-white/40 font-mono uppercase tracking-widest">{u.team}</span>
                    </label>
                  );
                })}
                {users.length === 0 && <div className="text-xs text-white/40 p-3">No users loaded.</div>}
              </div>
              <div className="flex justify-end mt-2">
                <Button onClick={saveAllowlist} disabled={savingSettings} className="bg-[#E11D2E] hover:bg-[#F03041]" data-testid={`vault-save-allowlist-${name}`}>
                  <Save size={13} className="mr-1" /> Save access list
                </Button>
              </div>
              <p className="text-[0.7rem] text-white/40 mt-2 leading-relaxed">
                Anyone on this list can unlock the section using the password. The base RBAC still applies for admins.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)} className="text-white/60">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
